import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  ArrowUpRight,
  Lock,
  Star,
  LineChart as LineChartIcon,
  Search as SearchIcon,
  Info,
  Gauge,
  ShieldCheck,
  TrendingUp,
  BookOpen,
  Zap,
} from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import StockSearch from "@/components/StockSearch";
import CompanyIdentityHeader from "@/components/analyze/CompanyIdentityHeader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api from "@/lib/api";

// ---- REAL analyze presentational components, reused verbatim ----
// The v1.8 / v1.9 versions of this page rebuilt these visuals from
// scratch, which is exactly what we must NOT do. v2 delegates
// everything below the hero to the same components that render on
// /analyze/:ticker so the preview is a true locked-state of the
// real product, not a marketing recreation.
import ScorePanel from "@/components/ScorePanel";
import InstitutionalSnapshot from "@/components/InstitutionalSnapshot";
import PriceChart from "@/components/PriceChart";
import AlphaSignals from "@/components/AlphaSignals";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import KeyDriversPanel from "@/components/KeyDriversPanel";
import GrowthChart from "@/components/GrowthChart";
import KpiCard from "@/components/KpiCard";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/lib/i18n";
import {
  methodologyShortLabel,
  conflictsLabel,
  correctionsLabel,
} from "@/lib/researchTransparencyCopy";
import { Disclaimer } from "@/components/Disclaimer";

/**
 * AnalyzeLanding — Phase 8.LP.LR v2 (real-preview rebuild)
 * ---------------------------------------------------------
 * Dedicated /analyze entry & product-preview page.
 *
 * Design principle: this page **reuses the same presentational
 * components** that render the real /analyze/:ticker experience,
 * driven by a static PayPal (PYPL) preview payload. There are no
 * bespoke replicas of the score hero, snapshot, chart, alpha
 * signals, insights, drivers, growth charts, or KPI cards — every
 * card the visitor sees is the actual production component in a
 * locked preview state (Free tier / aiFull=false), so the visitor
 * sees exactly what a Free user sees on their own analysis.
 *
 * Component reuse map:
 *
 *   Hero                          — custom (search entry only)
 *   Company identity band         — CompanyIdentityHeader (real)
 *   Score / Fair Value hero card  — ScorePanel (real)
 *   Institutional Snapshot        — InstitutionalSnapshot (real)
 *   Price vs Fair Value chart     — PriceChart (real, fetches PYPL)
 *   AI Alpha Signals              — AlphaSignals aiFull=false (real)
 *   AI Insights + tabs (locked)   — AIInsightsPanel full=false (real)
 *   Key drivers behind the score  — KeyDriversPanel (real)
 *   Revenue / Earnings charts     — GrowthChart (real)
 *   KPI grid                      — KpiCard (real, x12)
 *   Middle CTA + Bottom CTA       — small custom widgets
 *
 * The /analyze/:ticker result page is completely untouched.
 */

// -----------------------------------------------------------
// Static PYPL preview payload — shaped to match the exact
// contract each real component expects.
// -----------------------------------------------------------
const PYPL_IDENTITY = {
  ticker: "PYPL",
  name: "PayPal Holdings, Inc.",
  logo_url: "https://images.financialmodelingprep.com/symbol/PYPL.png",
  exchange: "NASDAQ",
  sector: "Financials",
  industry: "Financial - Credit Services",
  country: "US",
};

const PYPL_PRICE = 44.28;
// Header price string — the real /analyze/:ticker page renders
// the header price with a leading "$" (CompanyIdentityHeader takes
// a formatted string, not a number). Passing the pre-formatted
// value here keeps the landing header in lockstep with the real
// page — see reference PayPal analysis screenshot.
const PYPL_PRICE_STR = "$44.28";

const PYPL_VALUATION = {
  ai_score: 76,
  signal: "Opportunity",
  status: "Undervalued",
  upside_pct: 38.4,
  fair_value: 61.30,
  fair_value_low: 53.95,
  fair_value_high: 68.66,
  fair_value_delta: 17.02,
  risk_level: "Medium",
  sub_scores: {
    valuation: 95,
    quality: 66,
    growth: 52,
    safety: 75,
  },
  institutional: {
    available: true,
    classification: { label: "Value" },
    quality_gate: {
      status: "Pass",
      reasons: ["No material quality concerns detected"],
    },
    confidence: { label: "Medium", score: 66 },
    confidence_label: "Medium",
    confidence_score: 66,
    composite_score: 82,
    risk: {
      regime: "Low",
      helper: "Elevated beta 1.3",
    },
    valuation: {
      fair_value_confidence: "Low",
      fair_value_spread: 0.76,
      upside_pct: 38.4,
    },
    valuation_methods: [
      { name: "DCF", weight: 0.4 },
      { name: "Sector P/E", weight: 0.4 },
      { name: "Price/Book", weight: 0.2 },
    ],
    reasoning: [
      "Classified as Value — valuation methods adapted accordingly.",
      "Fair value blended from: DCF, Sector P/E, Price/Book.",
      "Implied upside of 40.4% vs current price (Low confidence).",
      "Quality gate: Pass — No material quality concerns detected.",
      "Risk regime: Low — Elevated beta 1.3.",
      "Overall confidence is Medium (66%).",
    ],
    detected_risks: [],
    hard_vetoes: [],
    soft_vetoes: [],
  },
  diagnostics: {
    fcf_quality: { fcf_yield: 0.0994, fcf_to_net_income: 0.74 },
    profitability: { roic: 0.15 },
    revenue_growth: { revenue_growth_yoy: 0.043 },
    relative_valuation: { pe_to_sector_ratio: 0.82 },
    balance_sheet: { debt_to_equity: 0.49 },
  },
};

// A 2-sentence Free-tier Summary. Values are internally consistent
// with the score-hero payload ($44.28 / $61.30 / ~38% upside). The
// visible teaser stops after the "38% upside" clause; the blurred
// continuation overlay (rendered inside the same AI Insights card
// via an absolute-positioned child) communicates that deeper
// reasoning is locked behind signup.
const PYPL_AI = {
  summary:
    "PayPal Holdings, Inc. is classified as a Value stock in Financials · Credit Services, currently trading at $44.28 with a market cap of ~$39.06 billion. " +
    "Stockaily's blended valuation model reads the position as undervalued vs its intrinsic estimate of $61.30, implying about 38% upside.",
  bullets: [],
};

// Alpha signals payload — the real AlphaSignals component with
// aiFull=false blurs the `locked` portion of each row, exactly
// matching the "partial locking" behaviour requested. Ids match
// the canonical `signals.items.*` i18n keys so translation and
// backend-generated strings behave identically to the real
// /analyze/:ticker page.
const PYPL_ALPHA_SIGNALS = [
  {
    id: "low_confidence",
    severity: "warn",
    title: "Low valuation confidence",
    preview: "Valuation range is unusually wide due to",
    locked:
      "speculative cash-flow profile and limited visibility on terminal economics. AI suggests sizing positions ~40% below normal conviction.",
    params: {},
  },
  {
    id: "quality_value_alignment",
    severity: "ok",
    title: "High-conviction quality + value alignment",
    preview: "Both quality and valuation sub-scores sit in the top quartile —",
    locked:
      "a configuration that has historically outperformed the sector by ~6–9% annually over 3-year holding periods in our backtest universe.",
    params: {},
  },
  {
    id: "sector_percentile",
    severity: "info",
    title: "Sector percentile ranking",
    preview: "This name's AI Opportunity Score sits in the",
    locked:
      "top 24% of Financials stocks tracked by Stockaily — Pro shows live percentile vs 5 closest peers and historical accuracy of this signal.",
    params: { top_pct: 24, sector: "Financials" },
  },
];

const REVENUE_5Y = [
  { year: 2021, value: 25.371e9 },
  { year: 2022, value: 27.518e9 },
  { year: 2023, value: 29.771e9 },
  { year: 2024, value: 31.797e9 },
  { year: 2025, value: 33.169e9 },
];
const EARNINGS_5Y = [
  { year: 2021, value: 4.169e9 },
  { year: 2022, value: 2.419e9 },
  { year: 2023, value: 4.246e9 },
  { year: 2024, value: 4.147e9 },
  { year: 2025, value: 5.229e9 },
];

// Minimal metrics object — KeyDriversPanel primarily reads
// valuation.diagnostics, but it accepts a metrics prop too so we
// pass the essentials for completeness.
const PYPL_METRICS = {
  price: PYPL_PRICE,
  market_cap: 39.06e9,
  pe: 10.7,
  forward_pe: 0.3,
  eps: 5.46,
  revenue: 33.17e9,
  revenue_growth: 0.043,
  earnings_growth: 0.262,
  profit_margin: 0.158,
  dividend_yield: 0.0023,
  roic: 0.15,
  sector: "Financials",
};

export default function AnalyzeLanding() {
  const { user } = useAuth();
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const isSignedIn = !!user;

  // Landing 2c — Single-request data owner.
  //
  // The AnalyzeLanding route is the single data owner for the
  // logged-out Analyze Preview: it issues exactly one GET
  // /api/picks per mount and resolves the frozen top-opportunity
  // card (or the coherent fallback) once.  AnonymousAnalyzePreview
  // is a pure presentational consumer — it never fetches
  // /api/picks itself.  This guarantees the preview and any other
  // consumer share one payload, one loading completion time and
  // one fallback decision (no stale-response races).
  //
  // The signed-in variant renders AnalyzeHubSignedIn and never
  // needs the picks payload, so we skip the request in that
  // branch and avoid unnecessary backend traffic.
  const preview = useAnalyzeLandingPreview(!isSignedIn);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar showSearch={false} />
      {isSignedIn ? (
        <AnalyzeHubSignedIn user={user} t={t} navigate={navigate} />
      ) : (
        <AnonymousAnalyzePreview
          t={t}
          navigate={navigate}
          locale={locale}
          preview={preview}
        />
      )}
      <Footer />
    </div>
  );
}

// ============================================================
// Landing 2c — Bind Analyze Preview to the published Top
// Opportunity slot.  The logged-out Analyze Preview is fed by the
// same `/api/picks` endpoint that powers the Landing page's 4-card
// market grid, so both surfaces render the exact frozen card
// stored in the currently active ``homepage_market_snapshots``
// document.  When no active real snapshot exists yet (cold start
// / pre-launch), we fall back to the static PYPL preview as one
// coherent unit — layout, blur, lock and CTA behaviour are
// preserved in both modes.
//
// Fields consumed from the frozen top-opportunity card (via the
// public `/api/picks` projection):
//   ticker, name, sector, industry, price, market_cap,
//   valuation.{ai_score, signal, status, upside_pct, fair_value,
//              fair_value_low, fair_value_high, fair_value_delta,
//              risk_level, sub_scores}
// Nothing outside this whitelist is read.  In particular no draft
// data, no internal Mongo fields, and no extra readable analysis
// text is surfaced through this binding.
// ============================================================
function useAnalyzeLandingPreview(enabled) {
  // Resolved preview payload handed to AnonymousAnalyzePreview.
  // Starts as the static PYPL fallback so the first paint is
  // coherent even before /api/picks resolves; is atomically
  // replaced with the dynamic payload iff the response represents
  // a real active snapshot with a semantic top-opportunity card.
  const [preview, setPreview] = useState(() => buildPreview(null));

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/picks");
        if (cancelled) return;
        const data = res.data || {};
        // Resolve the coherent fallback decision ONCE from the
        // shared response.  When /api/picks reports the
        // pre-launch static fallback, we keep the static PYPL
        // preview — never mixing a published ticker with fallback
        // metrics or chart data.
        if (data.is_fallback) {
          setPreview(buildPreview(null));
          return;
        }
        // Identify the Top Opportunity by its semantic
        // classification flag (``is_top_pick``) — the same flag
        // the Landing 4-card grid uses.  Array position is a
        // last-resort tie-breaker only if the flag is absent
        // (defensive), never a primary identifier.
        const picks = Array.isArray(data.picks) ? data.picks : [];
        const top = picks.find((p) => p && p.is_top_pick === true) || null;
        if (!top || !top.ticker) {
          setPreview(buildPreview(null));
          return;
        }
        setPreview(buildPreview(top));
      } catch {
        // Any network / parse failure keeps the coherent static
        // PYPL fallback in place — never a mixed-source render.
        if (!cancelled) setPreview(buildPreview(null));
      }
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  return preview;
}

// Build the concrete preview object handed to every reused
// presentational component.  When ``top`` is null the caller
// receives the static PYPL preview verbatim.  When ``top`` is a
// real frozen card, only the whitelisted fields above are copied
// on top of the static illustrative defaults; everything else
// (institutional deep-dive, alpha signals text, growth charts,
// detailed KPI grid) stays as the accepted illustrative content
// which is either locked, blurred, or clearly labelled as an
// example preview.
function buildPreview(top) {
  if (!top) {
    return {
      dynamic: false,
      identity: PYPL_IDENTITY,
      price: PYPL_PRICE,
      priceStr: PYPL_PRICE_STR,
      valuation: PYPL_VALUATION,
      ai: PYPL_AI,
      alphaSignals: PYPL_ALPHA_SIGNALS,
      metrics: PYPL_METRICS,
      revenue5y: REVENUE_5Y,
      earnings5y: EARNINGS_5Y,
      marketCapLabel: "$39.06B",
    };
  }

  const v = top.valuation || {};
  const price = typeof top.price === "number" ? top.price : PYPL_PRICE;
  const priceStr = `$${price.toFixed(2)}`;
  const marketCap = typeof top.market_cap === "number" ? top.market_cap : null;

  const dynValuation = {
    // Numeric core — pulled from the frozen card.  Missing values
    // fall back to the illustrative PYPL defaults so no
    // downstream component ever receives ``null`` for a required
    // slot.  This preserves the ScorePanel / PriceChart contracts.
    ai_score: typeof v.ai_score === "number" ? v.ai_score : PYPL_VALUATION.ai_score,
    signal: v.signal || PYPL_VALUATION.signal,
    status: v.status || PYPL_VALUATION.status,
    upside_pct: typeof v.upside_pct === "number" ? v.upside_pct : PYPL_VALUATION.upside_pct,
    fair_value: typeof v.fair_value === "number" ? v.fair_value : PYPL_VALUATION.fair_value,
    fair_value_low: typeof v.fair_value_low === "number" ? v.fair_value_low : PYPL_VALUATION.fair_value_low,
    fair_value_high: typeof v.fair_value_high === "number" ? v.fair_value_high : PYPL_VALUATION.fair_value_high,
    fair_value_delta: typeof v.fair_value_delta === "number" ? v.fair_value_delta : PYPL_VALUATION.fair_value_delta,
    risk_level: v.risk_level || PYPL_VALUATION.risk_level,
    sub_scores: (v.sub_scores && typeof v.sub_scores === "object") ? v.sub_scores : PYPL_VALUATION.sub_scores,
    // The institutional / diagnostics blocks are illustrative
    // premium showcase content and are NOT part of the frozen
    // card / public projection — they stay as static PYPL data.
    institutional: PYPL_VALUATION.institutional,
    diagnostics: PYPL_VALUATION.diagnostics,
  };

  // Two-sentence Free-tier summary rebuilt from the frozen card's
  // safe fields only.  Mirrors the sentence structure of PYPL_AI
  // so the blur/lock overlay measurements are preserved.
  const nameStr = top.name || top.ticker;
  const sectorStr = top.sector || "";
  const industryStr = top.industry || "";
  const sectorIndustry = sectorStr && industryStr
    ? `${sectorStr} · ${industryStr}`
    : sectorStr || industryStr || "the market";
  const marketCapStr = marketCap
    ? (marketCap >= 1e12
        ? `$${(marketCap / 1e12).toFixed(2)} trillion`
        : `$${(marketCap / 1e9).toFixed(2)} billion`)
    : "N/A";
  const statusLower = String(dynValuation.status || "").toLowerCase();
  const upsideAbs = Math.abs(dynValuation.upside_pct);
  const dynSummary =
    `${nameStr} is classified as a ${dynValuation.signal} stock in ${sectorIndustry}, ` +
    `currently trading at $${price.toFixed(2)} with a market cap of ~${marketCapStr}. ` +
    `Stockaily's blended valuation model reads the position as ${statusLower} vs its ` +
    `intrinsic estimate of $${dynValuation.fair_value.toFixed(2)}, implying about ` +
    `${upsideAbs.toFixed(0)}% ${dynValuation.upside_pct >= 0 ? "upside" : "downside"}.`;

  // Sector-percentile Alpha Signal picks up the dynamic sector so
  // the visible headline is coherent with the identity band.
  const alphaSignals = PYPL_ALPHA_SIGNALS.map((s) =>
    s.id === "sector_percentile"
      ? { ...s, params: { ...(s.params || {}), sector: sectorStr || s.params?.sector } }
      : s,
  );

  // Synthesize a logo from the public FMP image CDN pattern so
  // the identity block keeps its polished feel.  This is the
  // same static CDN URL the PYPL preview uses today (see
  // PYPL_IDENTITY.logo_url) — no FMP API call is triggered.
  const dynIdentity = {
    ticker: top.ticker,
    name: nameStr,
    logo_url: `https://images.financialmodelingprep.com/symbol/${top.ticker}.png`,
    exchange: null,
    sector: sectorStr || null,
    industry: industryStr || null,
    country: null,
  };

  return {
    dynamic: true,
    identity: dynIdentity,
    price,
    priceStr,
    valuation: dynValuation,
    ai: { summary: dynSummary, bullets: [] },
    alphaSignals,
    metrics: { ...PYPL_METRICS, price, market_cap: marketCap || PYPL_METRICS.market_cap },
    revenue5y: REVENUE_5Y,
    earnings5y: EARNINGS_5Y,
    marketCapLabel: marketCap
      ? (marketCap >= 1e12
          ? `$${(marketCap / 1e12).toFixed(2)}T`
          : `$${(marketCap / 1e9).toFixed(2)}B`)
      : "$39.06B",
  };
}

// ============================================================
// Anonymous / logged-out preview: PayPal conversion example.
// (Preserves the entire v3 preview from before.)
// ============================================================
function AnonymousAnalyzePreview({ t, navigate, locale, preview }) {
  const isSignedIn = false;
  return (
    <>
      {/* ============================================================
          HERO — search entry point.
          - No autoFocus (v1.8 auto-opened dropdown covered preview).
          - pb-8/12 + preview pt-16/20 keeps any user-triggered
            dropdown safely above the preview label.
          ============================================================ */}
      <section data-testid="analyze-landing-hero" className="relative border-b border-border/40">
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[1000px] h-[420px] rounded-full opacity-[0.10]"
            style={{ background: "radial-gradient(closest-side, #3FAE89 0%, transparent 70%)" }}
          />
        </div>
        <div className="relative max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-8 sm:pb-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <span
              aria-hidden
              className="inline-flex items-center justify-center w-6 h-6 rounded-md border"
              style={{ borderColor: "rgba(63,174,137,0.35)", background: "rgba(63,174,137,0.10)", color: "#3FAE89" }}
            >
              <LineChartIcon className="w-3 h-3" />
            </span>
            <span data-testid="analyze-landing-kicker">{t("analyze_landing.hero_kicker")}</span>
          </div>
          <h1
            data-testid="analyze-landing-title"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            {t("analyze_landing.hero_title")}
          </h1>
          <p
            data-testid="analyze-landing-body"
            className="mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground/95 leading-relaxed"
          >
            {t("analyze_landing.hero_body")}
          </p>
          <div className="mt-6 sm:mt-8 max-w-2xl">
            <StockSearch
              testIdPrefix="analyze-landing-search"
              placeholder={t("analyze_landing.search_placeholder")}
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* ============================================================
          REAL-COMPONENT PREVIEW
          Container width mirrors the real /analyze/:ticker page
          (max-w-7xl + same horizontal padding), so every card
          below sits at the exact same width the visitor will see
          on their own analysis. This is the single most important
          fidelity fix — the v1 / v2 previews were narrower and
          that alone made the surface read as "landing mockup".

          Preview-scoped style block: suppresses the two Pro-first
          upsell surfaces that live INSIDE the reused real
          components (`alpha-signals-pro-tag`,
          `alpha-see-plans/alpha-upgrade-cta` dashed CTA row, and
          `ai-insights-locked-teaser`). The landing page shows its
          own single free-first mid-page CTA in their place, so
          removing them here is a display-only override — the
          real /analyze/:ticker route continues to render them
          untouched. Scoped by `[data-preview-mode="landing"]` so
          this CSS can never leak outside the landing preview.
          ============================================================ */}
      <style>{`
        [data-preview-mode="landing"] [data-testid="alpha-signals-pro-tag"],
        [data-preview-mode="landing"] [data-testid="ai-insights-locked-teaser"] {
          display: none !important;
        }
        /* The Alpha Signals footer "See plans / Upgrade to Pro" row
           is a sibling <div> containing the two upgrade buttons.
           :has() picks it up cleanly on all modern browsers. */
        [data-preview-mode="landing"] div:has(> * > [data-testid="alpha-upgrade-cta"]) {
          display: none !important;
        }
        /* v1.4.1 — Extend the AI Insights panel card so the
           in-card blur overlay has room to sit inside its bottom
           padding without collliding with the visible Summary. */
        [data-preview-mode="landing"] [data-testid="ai-narrative-tabs-free"] {
          min-height: 380px;
        }
      `}</style>

      <section
        data-testid="analyze-landing-preview"
        data-preview-mode="landing"
        className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-16"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-muted-foreground">
            <Sparkles className="w-3 h-3 text-[#3DEBFF]" />
            <span data-testid="analyze-landing-preview-label">
              {t("analyze_landing.preview_label")}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground/70">
            {t("analyze_landing.preview_caption")}
          </div>
        </div>

        {/* WS-1 canonical regulatory disclosure — attached to the
            unauthenticated stock-analysis preview surface. Renders
            once at the top of the preview card so the visitor sees it
            before any research signal, plus explicit unavailable
            status for methodology, conflicts and corrections until
            those artefacts are populated for the public preview. */}
        <div
          className="mb-5 rounded-xl border border-border/60 bg-card/40 px-4 py-3 space-y-2"
          data-testid="analyze-landing-ws1-disclosure"
          data-ws1-surface="unauth_analyze_landing"
          data-ws1-ticker={preview.identity.ticker}
        >
          <Disclaimer />
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-muted-foreground/70"
            data-testid="analyze-landing-ws1-status-line"
          >
            <span data-ws1-status="methodology" data-ws1-value="methodology_active_v1">
              {t("research_transparency.methodology_label")}: {methodologyShortLabel(t, "stockaily.methodology.v1")}
            </span>
            <span aria-hidden>·</span>
            <span data-ws1-status="conflicts" data-ws1-value="no_compensation_from_issuer">
              {t("research_transparency.conflicts_label")}: {conflictsLabel(t)}
            </span>
            <span aria-hidden>·</span>
            <span data-ws1-status="corrections" data-ws1-value="no_corrections_published">
              {t("research_transparency.corrections_label")}: {correctionsLabel(t, null)}
            </span>
          </div>
        </div>

        <div className="space-y-6" data-testid="analyze-landing-preview-card">
          {/* 1) Identity band — REAL component. Price string
              includes the "$" sign to match the real page. The
              "Live data" chip is deliberately NOT rendered here
              — the page is clearly an example preview, so a
              live-data chip would contradict the label above. */}
          <div className="rounded-2xl border border-border/60 bg-card/50 backdrop-blur-sm p-4 sm:p-6">
            <CompanyIdentityHeader
              identity={preview.identity}
              price={preview.priceStr}
              badges={
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    data-testid="analyze-landing-identity-status-chip"
                    className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                    style={{
                      color: "#3FAE89",
                      background: "rgba(63,174,137,0.10)",
                      border: "1px solid rgba(63,174,137,0.35)",
                    }}
                  >
                    {preview.valuation.status}
                  </span>
                </div>
              }
            />
          </div>

          {/* 2) Score / Fair Value hero — REAL ScorePanel */}
          <div data-testid="analyze-landing-score-hero">
            <ScorePanel valuation={preview.valuation} price={preview.price} animateKey="landing-preview" />
          </div>

          {/* 3) Institutional Snapshot — REAL component */}
          <div data-testid="analyze-landing-snapshot">
            <InstitutionalSnapshot institutional={preview.valuation.institutional} />
          </div>

          {/* 4) Price vs Fair Value — REAL PriceChart.
                Fetches real historical prices for the frozen top
                pick's ticker from the public /stocks/{ticker}/prices
                endpoint (no auth required, no quota consumption).
                If that endpoint fails the chart gracefully renders
                its own "chart data unavailable" fallback — same as
                the real analysis page. */}
          <div data-testid="analyze-landing-price-vs-fair-value">
            <PriceChart
              ticker={preview.identity.ticker}
              fairValue={preview.valuation.fair_value}
              fairLow={preview.valuation.fair_value_low}
              fairHigh={preview.valuation.fair_value_high}
              valuation={preview.valuation}
              height={420}
            />
          </div>

          {/* 5) AI Alpha Signals — REAL component in aiFull=false
                mode. Each signal shows its title + preview line
                intact and blurs only the deeper `locked` sentence,
                which is exactly the "partial locking" behaviour
                requested. Also renders its own Free-tier "See plans"
                / "Upgrade" CTA row. */}
          <div data-testid="analyze-landing-alpha-signals">
            <AlphaSignals
              signals={preview.alphaSignals}
              aiFull={false}
              ticker={preview.identity.ticker}
            />
          </div>

          {/* Mid compact centred CTA — signed-out visitors only.
              This is the ONE mid-page CTA. It's placed right after
              AI Alpha Signals (the first depth-teased premium area)
              so curiosity converts to click. Free-first copy: no
              "Upgrade to Pro" wording anywhere.

              Because the Pro-first CTA rows inside AlphaSignals and
              AIInsightsPanel are hidden by the scoped <style> block
              above, this is the ONLY mid-page CTA the visitor sees. */}
          {!isSignedIn && (
            <div className="flex justify-center">
              <div
                data-testid="analyze-landing-inline-cta"
                className="max-w-md w-full rounded-2xl border border-border/70 bg-card/70 backdrop-blur-sm px-5 py-4 flex flex-col items-center text-center gap-2 shadow-[var(--app-shadow-elev-1)]"
              >
                <div className="flex items-center gap-2 text-[13.5px] font-semibold tracking-tight">
                  <Lock className="w-3.5 h-3.5 text-[#3DEBFF]" />
                  {t("analyze_landing.inline_cta_title")}
                </div>
                <div className="text-[12px] text-muted-foreground">
                  {t("analyze_landing.inline_cta_subtext")}
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate("/signup")}
                  data-testid="analyze-landing-inline-cta-button"
                  className="mt-1 h-8 px-3 text-[12.5px]"
                >
                  <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" />
                  {t("analyze_landing.inline_cta_button")}
                </Button>
              </div>
            </div>
          )}

          {/* 6) AI Insights + Key Drivers — REAL two-column layout.
                v1.4.1: the blurred continuation lives INSIDE the
                AI Insights wrapper as an absolutely-positioned
                child sitting on the panel's own bottom padding.
                DOM-wise it's a child of `analyze-landing-ai-insights`
                (the wrapper that IS the AI Insights container), so
                there is no separate box below the panel — the blur
                visually overlays the lower portion of the same
                rounded card. Real filter:blur on actual text lines
                makes it read as "hidden real text", not a skeleton
                strip. Scoped `min-height` on the panel guarantees
                enough room for the overlay to sit clear of the
                visible 2-sentence teaser.
                v1.4.2 (Slice B/C follow-up): switched `items-stretch`
                → `items-start` on this row. `items-stretch` forced
                the AI Insights wrapper to match `KeyDriversPanel`'s
                shorter height; the AIInsightsPanel's natural content
                (Summary + tabs + WS-1 canonical disclosure at the
                bottom) is taller than that stretched height, so the
                WS-1 disclosure text overflowed the wrapper and
                visually landed on top of the Revenue chart in the
                next grid row. Letting each column take its natural
                height keeps the WS-1 disclosure inside its intended
                container without hiding, clipping or moving any
                required transparency content. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-stretch" data-testid="analyze-landing-ai-insights-row">
            <div
              data-testid="analyze-landing-ai-insights"
              className="relative h-full [&>*]:h-full"
            >
              <AIInsightsPanel
                ai={preview.ai}
                narrative={null}
                full={false}
                ticker={preview.identity.ticker}
                disclaimer={null}
                hideWs1Disclosure={true}
                showLockedContinuation={true}
              />
            </div>
            <div data-testid="analyze-landing-key-drivers-column" className="space-y-4">
              <div data-testid="analyze-landing-key-drivers">
                <KeyDriversPanel valuation={preview.valuation} metrics={preview.metrics} />
              </div>
              {/* Slice B/C v1.4.3 — compact right-column Research
                  Transparency disclosure. Sits directly BELOW
                  "Key drivers behind the score" and REPLACES the WS-1
                  disclosure that previously rendered inside the AI
                  Insights card (which visually implied that the
                  disclosure belonged to the AI-generated analysis
                  and extended the card vertically into the Revenue
                  chart). The content is unchanged: WS-1 compact
                  disclaimer + methodology / conflicts / corrections
                  status line + link to the central Research
                  Transparency page. Uses the same border/card tokens
                  as neighbouring panels so the disclosure feels
                  integrated but visibly secondary (dimmed text,
                  10-11px status line). No absolute/fixed positioning,
                  no z-index workaround. */}
              <div
                className="rounded-2xl border border-border/60 bg-card/40 p-4"
                data-testid="analyze-landing-right-transparency"
                data-ws1-surface="analyze_landing_right_column"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">
                  {t("research_transparency.methodology_label")}
                </div>
                <Disclaimer compact />
                <div
                  className="mt-3 pt-3 border-t border-border/40 flex flex-col gap-y-1 text-[11px] text-muted-foreground/85 leading-relaxed"
                  data-testid="analyze-landing-right-transparency-status"
                >
                  <span data-ws1-status="methodology" data-ws1-value="methodology_active_v1">
                    <span className="uppercase tracking-wider text-[10px] text-muted-foreground/70">
                      {t("research_transparency.methodology_label")}:
                    </span>{" "}
                    {methodologyShortLabel(t, "stockaily.methodology.v1")}
                  </span>
                  <span data-ws1-status="conflicts" data-ws1-value="no_compensation_from_issuer">
                    <span className="uppercase tracking-wider text-[10px] text-muted-foreground/70">
                      {t("research_transparency.conflicts_label")}:
                    </span>{" "}
                    {conflictsLabel(t)}
                  </span>
                  <span
                    data-ws1-status="corrections"
                    data-ws1-value="no_corrections_published"
                  >
                    <span className="uppercase tracking-wider text-[10px] text-muted-foreground/70">
                      {t("research_transparency.corrections_label")}:
                    </span>{" "}
                    {correctionsLabel(t, null)}
                  </span>
                </div>
                <div className="mt-3">
                  <Link
                    to={locale === "de" ? "/de/research-transparenz" : "/en/research-transparency"}
                    className="text-[11px] font-medium text-[#3182CE] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-sm"
                    data-testid="analyze-landing-right-transparency-link"
                  >
                    {t("research_transparency.page_title")} →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* 7) Revenue + Earnings — REAL GrowthChart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div data-testid="analyze-landing-revenue-chart">
              <GrowthChart
                title={t("analyze.chart.revenue")}
                data={preview.revenue5y}
                color="#3182CE"
                testId="growth-chart-revenue"
              />
            </div>
            <div data-testid="analyze-landing-earnings-chart">
              <GrowthChart
                title={t("analyze.chart.earnings")}
                data={preview.earnings5y}
                color="rgba(255,255,255,0.55)"
                testId="growth-chart-earnings"
              />
            </div>
          </div>

          {/* 8) KPI grid — REAL KpiCard × 12.
                v1.3 polish: every tile now carries an `infoKey`
                so the small (i) tooltip surfaces beginner-friendly
                explanations, matching the real /analyze KPI grid.
                (kpi-explanations.js already ships all 12 keys.) */}
          <div
            data-testid="analyze-landing-kpi-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            <KpiCard label="Market Cap" value={preview.marketCapLabel} testId="kpi-market-cap" rawValue={preview.metrics.market_cap} infoKey="market_cap" />
            <KpiCard label="P/E" value="10.7x" testId="kpi-pe" rawValue={10.7} infoKey="pe" />
            <KpiCard label="Forward P/E" value="0.3x" testId="kpi-forward-pe" rawValue={0.3} infoKey="forward_pe" />
            <KpiCard label="EPS" value="$5.46" testId="kpi-eps" rawValue={5.46} infoKey="eps" />
            <KpiCard label="Revenue" value="$33.17B" testId="kpi-revenue" rawValue={33.17e9} infoKey="revenue" />
            <KpiCard label="Revenue Growth" value="4.3%" testId="kpi-rev-growth" rawValue={0.043} accent="text-[#3FAE89]" infoKey="revenue_growth" />
            <KpiCard label="Earnings Growth" value="26.2%" testId="kpi-earnings-growth" rawValue={0.262} accent="text-[#3FAE89]" infoKey="earnings_growth" />
            <KpiCard label="Profit Margin" value="15.8%" testId="kpi-profit-margin" rawValue={0.158} infoKey="profit_margin" />
            <KpiCard label="Dividend Yield" value="0.23%" testId="kpi-div-yield" rawValue={0.0023} infoKey="dividend_yield" />
            <KpiCard label="ROIC" value="15.0%" testId="kpi-roic" rawValue={0.15} accent="text-[#3FAE89]" infoKey="roic" />
            <KpiCard label="FCF Quality" value="66 / 100" testId="kpi-fcf-quality" rawValue={66} infoKey="fcf_quality" />
            <KpiCard label="Sector Percentile" value="Top 24%" testId="kpi-sector-percentile" rawValue={76} infoKey="sector_percentile" />
          </div>
        </div>

        {/* ==========================================================
            Final CTA — single, calm, compact.
            No large duplicate CTA cards.
            ========================================================== */}
        <div
          data-testid={
            isSignedIn ? "analyze-landing-cta-signed-in" : "analyze-landing-cta-signed-out"
          }
          className="mt-10 rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div>
            <div className="font-display text-base sm:text-lg tracking-tight">
              {isSignedIn
                ? t("analyze_landing.cta_signed_in_title")
                : t("analyze_landing.cta_signed_out_title")}
            </div>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              {isSignedIn
                ? t("analyze_landing.cta_signed_in_body")
                : t("analyze_landing.cta_signed_out_body")}
            </p>
          </div>
          {isSignedIn ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/watchlist")}
              data-testid="analyze-landing-cta-secondary"
            >
              <Star className="w-3.5 h-3.5 mr-1.5" />
              {t("analyze_landing.cta_signed_in_secondary")}
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                size="sm"
                onClick={() => navigate("/signup")}
                data-testid="analyze-landing-cta-primary"
              >
                <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" />
                {t("analyze_landing.cta_signed_out_primary")}
              </Button>
              <Link
                to="/pricing"
                data-testid="analyze-landing-cta-secondary"
                className="inline-flex items-center justify-center h-8 px-3 rounded-md text-[12.5px] border border-border/60 hover:bg-secondary/60 transition-colors"
              >
                {t("analyze_landing.cta_signed_out_secondary")}
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// ============================================================
// Analyze Hub — signed-in view.
// A cleaner, non-conversion-heavy landing page for members:
//   * Hero + StockSearch
//   * Compact plan/usage widget
//   * Recent analyses (empty state until a safe endpoint exists)
//   * Educational "How to read the AI Opportunity Score" block
// ============================================================
function AnalyzeHubSignedIn({ user, t, navigate }) {
  const [usage, setUsage] = useState(null);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentLoaded, setRecentLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/usage");
        if (!cancelled) setUsage(res.data || null);
      } catch {
        if (!cancelled) setUsage(null);
      } finally {
        if (!cancelled) setUsageLoaded(true);
      }
    })();
    // Real recent-analyses fetch (Phase 8.LP.LR v1.4). The endpoint
    // is read-only, user-scoped, capped at 5, and never triggers
    // FMP / LLM / valuation. See backend/routers/analyze_history.py.
    (async () => {
      try {
        const res = await api.get("/analyze/recent");
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        if (!cancelled) setRecent(items);
      } catch {
        if (!cancelled) setRecent([]);
      } finally {
        if (!cancelled) setRecentLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const plan = String(usage?.plan || user?.plan || "free").toLowerCase();
  const unlimited = plan === "pro" || plan === "premium" || !!usage?.unlimited;
  const remaining = typeof usage?.remaining === "number" ? usage.remaining : null;
  const limit = typeof usage?.limit === "number" ? usage.limit : null;
  const isFirstPeriod = !!usage?.is_first_period;

  return (
    <>
      {/* Hero + search + usage widget */}
      <section
        data-testid="analyze-hub-hero"
        className="relative border-b border-border/40"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div
            className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[1000px] h-[420px] rounded-full opacity-[0.10]"
            style={{ background: "radial-gradient(closest-side, #3FAE89 0%, transparent 70%)" }}
          />
        </div>
        <div className="relative max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-8 sm:pb-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <span
              aria-hidden
              className="inline-flex items-center justify-center w-6 h-6 rounded-md border"
              style={{ borderColor: "rgba(63,174,137,0.35)", background: "rgba(63,174,137,0.10)", color: "#3FAE89" }}
            >
              <LineChartIcon className="w-3 h-3" />
            </span>
            <span>{t("analyze_landing.hero_kicker")}</span>
          </div>
          <h1
            data-testid="analyze-hub-title"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            {t("analyze_landing.hero_title")}
          </h1>
          <p className="mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground/95 leading-relaxed">
            {t("analyze_hub.hero_body")}
          </p>
          <div className="mt-6 sm:mt-8 max-w-2xl">
            <StockSearch
              testIdPrefix="analyze-hub-search"
              placeholder={t("analyze_landing.search_placeholder")}
              className="w-full"
            />
          </div>

          {/* Compact plan/usage module */}
          <div
            data-testid="analyze-hub-usage"
            className="mt-4 max-w-2xl inline-flex items-center gap-3 flex-wrap rounded-xl border border-border/60 bg-card/50 backdrop-blur-sm px-4 py-2.5"
          >
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider"
              style={{
                color: unlimited ? "#3FAE89" : "#3182CE",
                background: unlimited ? "rgba(63,174,137,0.10)" : "rgba(49,130,206,0.10)",
                border: `1px solid ${unlimited ? "rgba(63,174,137,0.35)" : "rgba(49,130,206,0.30)"}`,
              }}
              data-testid="analyze-hub-plan-chip"
            >
              {unlimited ? (plan === "premium" ? "Premium" : "Pro") : "Free"}
            </span>
            {!usageLoaded ? (
              <span className="text-[13px] text-muted-foreground">{t("analyze_hub.usage_loading")}</span>
            ) : unlimited ? (
              <span
                data-testid="analyze-hub-usage-unlimited"
                className="text-[13px] text-foreground/90"
              >
                {t("analyze_hub.usage_unlimited")}
              </span>
            ) : remaining !== null ? (
              remaining > 0 ? (
                <span
                  data-testid="analyze-hub-usage-remaining"
                  className="text-[13px] text-foreground/90"
                >
                  <span className="tabular font-semibold">{remaining}</span>
                  <span className="text-muted-foreground">
                    {" "}of {limit ?? "—"} {t("analyze_hub.usage_analyses_remaining")}
                  </span>
                  {isFirstPeriod ? (
                    <span className="text-muted-foreground/80 ml-1.5">
                      · {t("analyze_hub.usage_first_period")}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/80 ml-1.5">
                      · {t("analyze_hub.usage_rolling_window")}
                    </span>
                  )}
                </span>
              ) : (
                <span
                  data-testid="analyze-hub-usage-exhausted"
                  className="inline-flex items-center gap-2 text-[13px] text-[#D9A441]"
                >
                  <Lock className="w-3.5 h-3.5" />
                  {t("analyze_hub.usage_exhausted")}
                  <Link
                    to="/pricing"
                    className="underline underline-offset-2 text-foreground/90 hover:text-foreground"
                    data-testid="analyze-hub-usage-exhausted-link"
                  >
                    {t("analyze_hub.usage_exhausted_link")}
                  </Link>
                </span>
              )
            ) : (
              <span className="text-[13px] text-muted-foreground">{t("analyze_hub.usage_unavailable")}</span>
            )}
          </div>
        </div>
      </section>

      {/* Body: recent analyses + educational block */}
      <section
        data-testid="analyze-hub-body"
        className="relative max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 pb-16"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* Recent analyses */}
          <div
            data-testid="analyze-hub-recent"
            className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-5 sm:p-6"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#3DEBFF]" />
                <div className="text-base font-semibold tracking-tight">
                  {t("analyze_hub.recent_title")}
                </div>
              </div>
            </div>
            {!recentLoaded ? (
              <div className="space-y-2" data-testid="analyze-hub-recent-loading">
                <div className="h-10 rounded-lg bg-white/5 animate-pulse" />
                <div className="h-10 rounded-lg bg-white/5 animate-pulse" />
                <div className="h-10 rounded-lg bg-white/5 animate-pulse" />
              </div>
            ) : recent.length === 0 ? (
              <div
                data-testid="analyze-hub-recent-empty"
                className="rounded-xl border border-dashed border-border/60 bg-black/20 px-5 py-8 flex flex-col items-center text-center gap-3"
              >
                <SearchIcon className="w-5 h-5 text-muted-foreground/60" />
                <div className="text-[13.5px] text-foreground/85 max-w-sm">
                  {t("analyze_hub.recent_empty_body")}
                </div>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="analyze-hub-recent-list">
                {recent.map((r) => (
                  <li
                    key={r.ticker}
                    data-testid={`analyze-hub-recent-row-${r.ticker}`}
                    className="flex items-center justify-between rounded-lg border border-border/60 bg-black/25 px-3 py-2 gap-3"
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      {r.logo_url ? (
                        <img
                          src={r.logo_url}
                          alt=""
                          className="w-8 h-8 rounded-md object-contain bg-white/5 border border-border/50 shrink-0"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-md bg-white/5 border border-border/50 flex items-center justify-center text-[11px] font-semibold text-muted-foreground shrink-0">
                          {r.ticker.slice(0, 2)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono tabular text-sm font-semibold truncate">{r.ticker}</span>
                          {r.status && (
                            <span
                              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border shrink-0"
                              style={{
                                color: /undervalued/i.test(String(r.status)) ? "#3FAE89"
                                      : /overvalued/i.test(String(r.status)) ? "#E5484D"
                                      : "#A8AEB7",
                                background: "rgba(0,0,0,0.25)",
                                borderColor: "rgba(255,255,255,0.14)",
                              }}
                            >
                              {r.status}
                            </span>
                          )}
                        </div>
                        {r.name && (
                          <div className="truncate text-muted-foreground text-[11.5px]">{r.name}</div>
                        )}
                      </div>
                      {typeof r.ai_score === "number" && (
                        <div className="text-right shrink-0 tabular text-[12.5px] text-[#3FAE89] font-semibold">
                          {r.ai_score}
                          <span className="text-muted-foreground/70 text-[10.5px] font-normal">/100</span>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/analyze/${r.ticker}`)}
                      data-testid={`analyze-hub-recent-open-${r.ticker}`}
                      className="text-[12px] text-[#3DEBFF] hover:text-white transition-colors inline-flex items-center gap-1 shrink-0"
                    >
                      {t("analyze_hub.recent_open")} <ArrowUpRight className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Educational — How to read the AI Opportunity Score */}
          <div
            data-testid="analyze-hub-education"
            className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-5 sm:p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-[#3FAE89]" />
              <div className="text-base font-semibold tracking-tight">
                {t("analyze_hub.education_title")}
              </div>
            </div>
            <div className="text-[13px] text-muted-foreground mb-4">
              {t("analyze_hub.education_intro")}
            </div>
            <ul className="space-y-2.5">
              <EducationRow
                icon={<Gauge className="w-3.5 h-3.5" />}
                tone="ok"
                title={t("analyze_hub.education_score_title")}
                body={t("analyze_hub.education_score_body")}
              />
              <EducationRow
                icon={<TrendingUp className="w-3.5 h-3.5" />}
                tone="info"
                title={t("analyze_hub.education_fairvalue_title")}
                body={t("analyze_hub.education_fairvalue_body")}
              />
              <EducationRow
                icon={<ShieldCheck className="w-3.5 h-3.5" />}
                tone="warn"
                title={t("analyze_hub.education_risk_title")}
                body={t("analyze_hub.education_risk_body")}
              />
            </ul>
            <div className="mt-4 flex flex-wrap gap-2 text-[11.5px] text-muted-foreground">
              <ConceptTooltip
                label={t("analyze_hub.concept_fair_value")}
                body={t("analyze_hub.concept_fair_value_help")}
                testId="hub-concept-fair-value"
              />
              <ConceptTooltip
                label={t("analyze_hub.concept_score")}
                body={t("analyze_hub.concept_score_help")}
                testId="hub-concept-score"
              />
              <ConceptTooltip
                label={t("analyze_hub.concept_risk")}
                body={t("analyze_hub.concept_risk_help")}
                testId="hub-concept-risk"
              />
              <ConceptTooltip
                label={t("analyze_hub.concept_bullbear")}
                body={t("analyze_hub.concept_bullbear_help")}
                testId="hub-concept-bullbear"
              />
              <ConceptTooltip
                label={t("analyze_hub.concept_drivers")}
                body={t("analyze_hub.concept_drivers_help")}
                testId="hub-concept-drivers"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function EducationRow({ icon, tone, title, body }) {
  const tones = {
    ok: { fg: "#3FAE89", bg: "rgba(63,174,137,0.08)", border: "rgba(63,174,137,0.28)" },
    warn: { fg: "#D9A441", bg: "rgba(217,164,65,0.08)", border: "rgba(217,164,65,0.28)" },
    info: { fg: "#3182CE", bg: "rgba(49,130,206,0.08)", border: "rgba(49,130,206,0.28)" },
  };
  const cfg = tones[tone] || tones.info;
  return (
    <li className="flex gap-3 items-start">
      <span
        className="inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0 mt-0.5"
        style={{ color: cfg.fg, background: cfg.bg, border: `1px solid ${cfg.border}` }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: cfg.fg }}>{title}</div>
        <div className="text-[12.5px] text-foreground/85 leading-relaxed mt-0.5">{body}</div>
      </div>
    </li>
  );
}

function ConceptTooltip({ label, body, testId }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-black/25 px-2 py-0.5 hover:border-border/80"
          >
            <Info className="w-3 h-3 text-muted-foreground/70" />
            <span>{label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-[12.5px] leading-relaxed">
          {body}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

